import { coerceNumberProperty } from '@angular/cdk/coercion';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';

import { PhxBadgeSize, PhxBadgeIntent, PhxBadgeContrast, PhxBadgeDefaultValues, PhxBadgeShape, PhxBadgeWeight } from './phx-badge.types';
import { Cross, HourGlass } from '../icon/icon-map';
import mixinDisabled from '../../mixins/disabled';
import { PHX_BACKGROUND } from '../../constants';
import { PhxBackground } from '../../types';

export const phxBadgeDefaultValues: PhxBadgeDefaultValues = {
  intent: 'accent',
  contrast: 'high',
  size: 'medium',
}

const phxBadgeIconSizes: Record<PhxBadgeSize, number> = {
  'large': 16,
  'medium': 12,
  'small': 12,
}

const phxBadgeCloseIconSizes: Record<PhxBadgeSize, number> = {
  'large': 20,
  'medium': 16,
  'small': 12,
}

const baseMixin = mixinDisabled(
  class {
    constructor() { }
  }
);
@Component({
  selector: 'phx-badge',
  templateUrl: 'phx-badge.component.html',
  styleUrls: ['phx-badge.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  inputs: ['disabled'],
  host: {
    class: 'phx-badge',
    '[class.phx-badge-large]': 'phxSize === "large"',
    '[class.phx-badge-medium]': 'phxSize === "medium"',
    '[class.phx-badge-small]': 'phxSize === "small"',
    '[class.phx-badge-hide-zero]': '!isShowCount && !phxText && phxIntent !== "calculating"',
    '[class.phx-badge-accent]': 'phxIntent === "accent"',
    '[class.phx-badge-neutral]': 'phxIntent === "neutral"',
    '[class.phx-badge-positive]': 'phxIntent === "positive"',
    '[class.phx-badge-negative]': 'phxIntent === "negative"',
    '[class.phx-badge-caution]': 'phxIntent === "caution"',
    '[class.phx-badge-warning]': 'phxIntent === "warning"',
    '[class.phx-badge-fair]': 'phxIntent === "fair"',
    '[class.phx-badge-good]': 'phxIntent === "good"',
    '[class.phx-badge-excellent]': 'phxIntent === "excellent"',
    '[class.phx-badge-calculating]': 'phxIntent === "calculating"',
    '[class.phx-badge-contrast-high]': 'phxContrast === "high"',
    '[class.phx-badge-contrast-low]': 'phxContrast === "low"',
    '[class.phx-badge-contrast-transparent]': 'phxContrast === "transparent"',
    '[class.phx-badge-weight-bold]': 'phxWeight === "bold"',
    '[class.phx-badge-weight-medium]': 'phxWeight === "medium"',
    '[class.phx-badge-disabled]': 'disabled',
    '[class.phx-badge-with-icon]': 'phxIcon',
    '[class.phx-badge-rounded-square]': 'phxShape === "square"',
    '[class.phx-badge-rounded]': 'phxShape === "rounded"',
    '[class.phx-badge-dot]': 'phxShape === "dot"',
    '[class.phx-badge-dismissible]': 'phxDismissible',
    '[class.phx-badge-dismissed]': 'phxDismissible && phxDismissed',
  },
})
export class PhxBadgeComponent extends baseMixin {
  @Input() public phxIntent: PhxBadgeIntent = phxBadgeDefaultValues.intent;
  @Input() public phxContrast: PhxBadgeContrast = phxBadgeDefaultValues.contrast;
  @Input() public phxSize: PhxBadgeSize = phxBadgeDefaultValues.size;
  @Input() public phxText: string;
  @Input() public phxIcon: string;
  @Input() public phxShape: PhxBadgeShape = 'rounded';
  @Input() public phxShowZero: boolean = false;
  @Input() public phxWeight: PhxBadgeWeight = 'bold';
  @Input() public phxDismissible: boolean = false;
  @Input() public phxDismissed: boolean = false;
  @Input()
  public get phxCount(): number {
    return this._count;
  }
  public set phxCount(value: number) {
    this._count = coerceNumberProperty(value);
  }

  @Output() phxDismissedChange = new EventEmitter<void>();

  public readonly CloseIcon = Cross;
  public readonly hourGlassIcon = HourGlass;
  public readonly iconSizes = phxBadgeIconSizes;
  public readonly closeIconSizes = phxBadgeCloseIconSizes;

  private _count: number;

  ngOnChanges(changes: SimpleChanges) {
    const { phxShape } = changes;

    if (phxShape) {
      // Show the zero values if the phxShape is square
      this.phxShowZero = this.phxShape === 'square' ? true : this.phxShowZero;
    }
  }

  public get isShowCount(): boolean {
    return (this.phxShowZero || Boolean(this.phxCount)) && !this.phxText;
  }

  public get dividerBackground(): PhxBackground {
    if (this.disabled || this.phxContrast === 'low' || this.phxContrast === 'transparent') return PHX_BACKGROUND.STANDARD;
    if (this.phxIntent === 'accent' || this.phxIntent === 'negative' || this.phxIntent === 'positive') return PHX_BACKGROUND.BOLD;
    if (this.phxIntent === 'caution') return PHX_BACKGROUND.MEDIUM;
    if (this.phxIntent === 'neutral') return PHX_BACKGROUND.INVERTED;
    return PHX_BACKGROUND.STANDARD;
  }

  public onBadgeDismiss(): void {
    if (this.disabled) return;
    this.phxDismissed = true;
    this.phxDismissedChange.emit();
  }
}
